import { z } from "zod";
import { automatedRunSettingsSchema } from "../automation";

export * from "../automation";

/**
 * Unified automation rule model (AU1).
 * When (trigger) -> If (conditions) -> Then (actions), plus guardrails and owner.
 */
export const ruleTriggerTypes = [
  "schedule",
  "webhook",
  "repository-event",
  "work-item",
  "run-event",
  "approval-wait",
  "spend-threshold",
  "eval-result",
  "connection-health",
] as const;
export const ruleTriggerTypeSchema = z.enum(ruleTriggerTypes);
export type RuleTriggerType = z.infer<typeof ruleTriggerTypeSchema>;

export const ruleTriggerSchema = z.object({
  type: ruleTriggerTypeSchema,
  cron: z.string().trim().min(1).max(120).optional(),
  event: z.string().trim().min(1).max(120).optional(),
  repositoryRef: z.string().trim().min(1).max(200).nullable().optional(),
  signingSecret: z.string().trim().min(16).max(400).optional(),
  thresholdPercent: z.number().int().min(1).max(100).optional(),
  workflowFilter: z.string().trim().optional(),
  waitingDuration: z.string().trim().optional(),
});
export type RuleTrigger = z.infer<typeof ruleTriggerSchema>;

/**
 * Repository event sources (AU4). One canonical name per GitHub delivery the
 * platform admits: a pull request opened, updated or merged, a push, a failed
 * check run, and a labelled issue. A `repository-event` rule names one of
 * these in its trigger event, and the repository in its trigger reference.
 */
export const repositoryEventTypes = [
  "pull_request.opened",
  "pull_request.updated",
  "pull_request.merged",
  "push",
  "check_run.failed",
  "issues.labeled",
] as const;
export const repositoryEventTypeSchema = z.enum(repositoryEventTypes);
export type RepositoryEventType = z.infer<typeof repositoryEventTypeSchema>;

export const conditionOperators = ["is", "is_not", "matches", "starts_with", "contains"] as const;
export const conditionOperatorSchema = z.enum(conditionOperators);
export type ConditionOperator = z.infer<typeof conditionOperatorSchema>;

/**
 * Longest payload candidate a `matches` condition evaluates. Webhook bodies reach 1 MiB, so an
 * unbounded candidate keeps even a rejected pattern expensive. An oversized candidate fails
 * closed without evaluation; every other operator compares the full value.
 */
export const maxConditionCandidateLength = 4096;

/** First-character approximation for one atom, used to spot overlapping quantified neighbours. */
interface ConditionPatternFirst {
  any: boolean;
  chars: Set<string>;
  ranges: Array<[number, number]>;
}

function emptyConditionPatternFirst(): ConditionPatternFirst {
  return { any: false, chars: new Set(), ranges: [] };
}

function anyConditionPatternFirst(): ConditionPatternFirst {
  return { any: true, chars: new Set(), ranges: [] };
}

function literalConditionPatternFirst(value: string): ConditionPatternFirst {
  return { any: false, chars: new Set([value]), ranges: [] };
}

function unionConditionPatternFirst(
  target: ConditionPatternFirst,
  source: ConditionPatternFirst,
): void {
  target.any = target.any || source.any;
  for (const char of source.chars) target.chars.add(char);
  for (const range of source.ranges) target.ranges.push(range);
}

function conditionPatternFirstIsEmpty(first: ConditionPatternFirst): boolean {
  return !first.any && first.chars.size === 0 && first.ranges.length === 0;
}

function codeInConditionRanges(code: number, ranges: Array<[number, number]>): boolean {
  return ranges.some(([low, high]) => code >= low && code <= high);
}

/** True when both atoms can start with the same character, so repeating them overlaps. */
function conditionPatternFirstsOverlap(
  a: ConditionPatternFirst,
  b: ConditionPatternFirst,
): boolean {
  if (conditionPatternFirstIsEmpty(a) || conditionPatternFirstIsEmpty(b)) return false;
  if (a.any || b.any) return true;
  for (const char of a.chars) {
    const code = char.codePointAt(0) ?? -1;
    if (b.chars.has(char) || codeInConditionRanges(code, b.ranges)) return true;
  }
  for (const [low, high] of a.ranges) {
    for (const char of b.chars) {
      const code = char.codePointAt(0) ?? -1;
      if (code >= low && code <= high) return true;
    }
    for (const [otherLow, otherHigh] of b.ranges) {
      if (low <= otherHigh && otherLow <= high) return true;
    }
  }
  return false;
}

type ConditionPatternEscape =
  | { kind: "zero"; next: number }
  | { kind: "first"; first: ConditionPatternFirst; next: number }
  | { kind: "invalid" }
  | { kind: "unsafe" };

function isHexDigit(char: string | undefined): boolean {
  return char !== undefined && /[0-9a-fA-F]/.test(char);
}

function mapConditionPatternEscape(
  pattern: string,
  index: number,
  inClass: boolean,
): ConditionPatternEscape {
  const esc = pattern[index + 1];
  if (esc === undefined) return { kind: "invalid" };
  if (!inClass && (esc === "b" || esc === "B")) return { kind: "zero", next: index + 2 };
  if (!inClass && esc >= "1" && esc <= "9") return { kind: "unsafe" };
  if (esc === "k") return { kind: "unsafe" };
  switch (esc) {
    case "d":
      return {
        kind: "first",
        first: { any: false, chars: new Set(), ranges: [[48, 57]] },
        next: index + 2,
      };
    case "w":
      return {
        kind: "first",
        first: {
          any: false,
          chars: new Set(["_"]),
          ranges: [
            [48, 57],
            [65, 90],
            [97, 122],
          ],
        },
        next: index + 2,
      };
    case "D":
    case "W":
    case "S":
    case "s":
      // Whitespace and negated classes are over-approximated as "any": safe, never under-rejects.
      return { kind: "first", first: anyConditionPatternFirst(), next: index + 2 };
    case "b":
      return { kind: "first", first: literalConditionPatternFirst("\b"), next: index + 2 };
    case "n":
      return { kind: "first", first: literalConditionPatternFirst("\n"), next: index + 2 };
    case "t":
      return { kind: "first", first: literalConditionPatternFirst("\t"), next: index + 2 };
    case "r":
      return { kind: "first", first: literalConditionPatternFirst("\r"), next: index + 2 };
    case "f":
      return { kind: "first", first: literalConditionPatternFirst("\f"), next: index + 2 };
    case "v":
      return { kind: "first", first: literalConditionPatternFirst("\v"), next: index + 2 };
    case "0":
      return { kind: "first", first: literalConditionPatternFirst("\0"), next: index + 2 };
    case "p":
    case "P": {
      if (pattern[index + 2] === "{") {
        const close = pattern.indexOf("}", index + 3);
        if (close === -1) return { kind: "invalid" };
        return { kind: "first", first: anyConditionPatternFirst(), next: close + 1 };
      }
      return { kind: "first", first: anyConditionPatternFirst(), next: index + 2 };
    }
    case "x": {
      let next = index + 2;
      for (let seen = 0; seen < 2 && isHexDigit(pattern[next]); seen += 1) next += 1;
      return { kind: "first", first: anyConditionPatternFirst(), next };
    }
    case "u": {
      if (pattern[index + 2] === "{") {
        const close = pattern.indexOf("}", index + 3);
        if (close === -1) return { kind: "invalid" };
        return { kind: "first", first: anyConditionPatternFirst(), next: close + 1 };
      }
      return {
        kind: "first",
        first: anyConditionPatternFirst(),
        next: Math.min(index + 6, pattern.length),
      };
    }
    case "c":
      return {
        kind: "first",
        first: anyConditionPatternFirst(),
        next: Math.min(index + 3, pattern.length),
      };
    default:
      return { kind: "first", first: literalConditionPatternFirst(esc), next: index + 2 };
  }
}

function parseConditionPatternClass(
  pattern: string,
  start: number,
): { first: ConditionPatternFirst; next: number } | null {
  let i = start + 1;
  if (pattern[i] === "^") {
    let j = i + 1;
    if (pattern[j] === "]") j += 1;
    while (j < pattern.length) {
      if (pattern[j] === "\\") {
        if (j + 1 >= pattern.length) return null;
        j += 2;
        continue;
      }
      if (pattern[j] === "]") return { first: anyConditionPatternFirst(), next: j + 1 };
      j += 1;
    }
    return null;
  }
  const first = emptyConditionPatternFirst();
  if (pattern[i] === "]") {
    first.chars.add("]");
    i += 1;
  }
  let pending: string | null = null;
  const flush = (): void => {
    if (pending !== null) {
      first.chars.add(pending);
      pending = null;
    }
  };
  while (i < pattern.length) {
    const char = pattern[i];
    if (char === undefined) return null;
    if (char === "]") {
      flush();
      return { first, next: i + 1 };
    }
    if (char === "\\") {
      const mapped = mapConditionPatternEscape(pattern, i, true);
      if (mapped.kind === "invalid" || mapped.kind === "unsafe") return null;
      if (mapped.kind === "first") unionConditionPatternFirst(first, mapped.first);
      i = mapped.next;
      continue;
    }
    if (char === "-" && pending !== null && i + 1 < pattern.length && pattern[i + 1] !== "]") {
      const end = pattern[i + 1] as string;
      if (end === "\\") {
        flush();
        first.any = true;
        const mapped = mapConditionPatternEscape(pattern, i + 1, true);
        if (mapped.kind === "invalid" || mapped.kind === "unsafe") return null;
        if (mapped.kind === "first") unionConditionPatternFirst(first, mapped.first);
        i = mapped.next;
        continue;
      }
      const low = pending.codePointAt(0) ?? 0;
      const high = end.codePointAt(0) ?? 0;
      if (low > high) return null;
      first.ranges.push([low, high]);
      pending = null;
      i += 2;
      continue;
    }
    flush();
    pending = char;
    i += 1;
  }
  return null;
}

/**
 * Reject patterns that risk catastrophic backtracking under the JavaScript RegExp engine, which
 * evaluates in polynomial or exponential time. A quantifier (`*`, `+`, `?`, `{m,n}`) is refused
 * when the atom it repeats can start with the same characters as the quantified atom before it,
 * as in the flat chain `^a*a*a*a*a*b$`, or when it repeats a group that already holds a
 * quantifier or a choice, as in `^(a+)+$` or `^(a|ab)+$`, since each shape forces the engine to
 * revisit the same input repeatedly on a near miss. The check is syntactic, so it runs in linear
 * time and never executes the pattern.
 *
 * Conservative by design: anything the scanner cannot parse cleanly is refused, including
 * backreferences and unknown group markers, and callers report the refusal as a named validation
 * failure rather than timing the match. Ordinary patterns, such as anchored literals, character
 * classes, and unquantified choices like `^(main|develop)$`, still pass.
 */
export function isSafeConditionPattern(pattern: string): boolean {
  interface Frame {
    assertion: boolean;
    quantified: boolean;
    choice: boolean;
    branchFirsts: ConditionPatternFirst[];
    branchFirst: ConditionPatternFirst | null;
    prevQuantified: boolean;
    prevFirst: ConditionPatternFirst;
    pending: boolean;
    pendingFirst: ConditionPatternFirst;
    justQuantified: boolean;
    lastWasLazy: boolean;
  }
  const freshFrame = (): Frame => ({
    assertion: false,
    quantified: false,
    choice: false,
    branchFirsts: [],
    branchFirst: null,
    prevQuantified: false,
    prevFirst: emptyConditionPatternFirst(),
    pending: false,
    pendingFirst: emptyConditionPatternFirst(),
    justQuantified: false,
    lastWasLazy: false,
  });
  const frames: Frame[] = [freshFrame()];
  const top = (): Frame => frames[frames.length - 1] as Frame;
  const commitPending = (frame: Frame): void => {
    if (frame.pending) {
      frame.prevQuantified = false;
      frame.prevFirst = frame.pendingFirst;
      frame.pending = false;
    }
    frame.justQuantified = false;
    frame.lastWasLazy = false;
  };
  const pushAtom = (first: ConditionPatternFirst): void => {
    const frame = top();
    commitPending(frame);
    if (frame.branchFirst === null) frame.branchFirst = first;
    frame.pendingFirst = first;
    frame.pending = true;
  };
  const applyQuantifier = (frame: Frame): boolean => {
    if (!frame.pending) return false;
    if (frame.prevQuantified && conditionPatternFirstsOverlap(frame.prevFirst, frame.pendingFirst))
      return false;
    frame.prevQuantified = true;
    frame.prevFirst = frame.pendingFirst;
    frame.pending = false;
    frame.quantified = true;
    frame.justQuantified = true;
    frame.lastWasLazy = false;
    return true;
  };
  let i = 0;
  while (i < pattern.length) {
    const char = pattern[i];
    if (char === undefined) return false;
    if (char === "(") {
      commitPending(top());
      const frame = freshFrame();
      let next = i + 1;
      if (pattern[next] === "?") {
        const marker = pattern[next + 1];
        if (marker === ":") {
          next += 2;
        } else if (marker === "=" || marker === "!") {
          frame.assertion = true;
          next += 2;
        } else if (marker === "<") {
          const third = pattern[next + 2];
          if (third === "=" || third === "!") {
            frame.assertion = true;
            next += 3;
          } else {
            const end = pattern.indexOf(">", next + 2);
            const name = end === -1 ? "" : pattern.slice(next + 2, end);
            if (end === -1 || !/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) return false;
            next = end + 1;
          }
        } else {
          return false;
        }
      }
      frames.push(frame);
      i = next;
      continue;
    }
    if (char === ")") {
      if (frames.length === 1) return false;
      const frame = frames.pop()!;
      commitPending(frame);
      let quantifiedAfter = false;
      let next = i + 1;
      if (
        next < pattern.length &&
        (pattern[next] === "*" || pattern[next] === "+" || pattern[next] === "?")
      ) {
        quantifiedAfter = true;
        next += 1;
      } else if (pattern[next] === "{") {
        const close = pattern.indexOf("}", next);
        if (close === -1) return false;
        if (!/^\{\d+(,\d*)?\}$/.test(pattern.slice(next, close + 1))) return false;
        quantifiedAfter = true;
        next = close + 1;
      }
      if (quantifiedAfter && pattern[next] === "?") next += 1;
      i = next;
      if (frame.assertion) {
        if (quantifiedAfter) return false;
        continue;
      }
      if (quantifiedAfter && (frame.quantified || frame.choice)) return false;
      const outer = top();
      if (frame.choice) outer.choice = true;
      if (frame.quantified) outer.quantified = true;
      const groupFirst = emptyConditionPatternFirst();
      for (const branch of frame.branchFirsts) unionConditionPatternFirst(groupFirst, branch);
      if (frame.branchFirst !== null) unionConditionPatternFirst(groupFirst, frame.branchFirst);
      pushAtom(groupFirst);
      if (quantifiedAfter && !applyQuantifier(outer)) return false;
      continue;
    }
    if (char === "|") {
      const frame = top();
      commitPending(frame);
      frame.branchFirsts.push(frame.branchFirst ?? emptyConditionPatternFirst());
      frame.branchFirst = null;
      frame.choice = true;
      frame.prevQuantified = false;
      frame.prevFirst = emptyConditionPatternFirst();
      i += 1;
      continue;
    }
    if (char === "*" || char === "+" || char === "?") {
      const frame = top();
      if (char === "?" && frame.justQuantified) {
        if (frame.lastWasLazy) return false;
        frame.lastWasLazy = true;
        i += 1;
        continue;
      }
      if (!applyQuantifier(frame)) return false;
      i += 1;
      continue;
    }
    if (char === "{") {
      const close = pattern.indexOf("}", i);
      const frame = top();
      if (close !== -1 && frame.pending && /^\{\d+(,\d*)?\}$/.test(pattern.slice(i, close + 1))) {
        i = close + 1;
        if (pattern[i] === "?") i += 1;
        if (!applyQuantifier(frame)) return false;
        continue;
      }
      pushAtom(literalConditionPatternFirst("{"));
      i += 1;
      continue;
    }
    if (char === "[") {
      const parsed = parseConditionPatternClass(pattern, i);
      if (parsed === null) return false;
      pushAtom(parsed.first);
      i = parsed.next;
      continue;
    }
    if (char === "\\") {
      const mapped = mapConditionPatternEscape(pattern, i, false);
      if (mapped.kind === "invalid" || mapped.kind === "unsafe") return false;
      if (mapped.kind === "zero") commitPending(top());
      else pushAtom(mapped.first);
      i = mapped.next;
      continue;
    }
    if (char === "^" || char === "$") {
      commitPending(top());
      i += 1;
      continue;
    }
    pushAtom(literalConditionPatternFirst(char));
    i += 1;
  }
  return frames.length === 1;
}

export const ruleConditionSchema = z
  .object({
    field: z.string().trim().min(1).max(100),
    operator: conditionOperatorSchema,
    value: z.string().trim().max(500),
  })
  .refine(
    (condition) => condition.operator !== "matches" || isSafeConditionPattern(condition.value),
    {
      message:
        "The matches pattern nests quantifiers and could stall evaluation; rewrite it without a quantified group inside a quantifier, such as ^a+$ instead of ^(a+)+$.",
      path: ["value"],
    },
  );
export type RuleCondition = z.infer<typeof ruleConditionSchema>;

const dayNames = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

/** Normalise a condition field so "Day of week", "day_of_week", and "day-of-week" agree. */
function normaliseField(field: string): string {
  return field
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "_");
}

function lookupField(
  event: Record<string, unknown>,
  field: string,
): { found: boolean; value: unknown } {
  if (Object.hasOwn(event, field)) return { found: true, value: event[field] };
  const lower = field.toLowerCase();
  const normalised = normaliseField(field);
  for (const key of Object.keys(event)) {
    if (key.toLowerCase() === lower || normaliseField(key) === normalised)
      return { found: true, value: event[key] };
  }
  if (field.includes(".")) {
    let current: unknown = event;
    for (const part of field.split(".")) {
      if (typeof current !== "object" || current === null || !(part in current))
        return { found: false, value: undefined };
      current = (current as Record<string, unknown>)[part];
    }
    return { found: true, value: current };
  }
  return { found: false, value: undefined };
}

function candidatesOf(value: unknown): string[] | null {
  if (value === undefined || value === null) return null;
  if (Array.isArray(value))
    return value.flatMap((item) =>
      item === undefined || item === null || typeof item === "object" ? [] : [String(item)],
    );
  if (typeof value === "object") return [JSON.stringify(value)];
  return [String(value)];
}

function describeReceived(value: unknown): string {
  const text = Array.isArray(value) ? value.map((item) => String(item)).join(", ") : String(value);
  return text.length > 120 ? `${text.slice(0, 117)}...` : text;
}

export interface ConditionVerdict {
  matched: boolean;
  reason: string | null;
}

/**
 * Evaluate one condition against an event payload. Event data is untrusted input: it is only
 * compared, never executed. A missing field fails closed with a named reason.
 */
export function evaluateRuleCondition(
  condition: RuleCondition,
  event: Record<string, unknown>,
  now: Date = new Date(),
): ConditionVerdict {
  const normalised = normaliseField(condition.field);
  let lookup = lookupField(event, condition.field);
  if (
    !lookup.found &&
    (normalised === "day_of_week" ||
      normalised === "weekday" ||
      normalised === "dayofweek" ||
      normalised === "day")
  ) {
    lookup = { found: true, value: dayNames[now.getUTCDay()] };
  }
  if (!lookup.found || lookup.value === undefined || lookup.value === null)
    return {
      matched: false,
      reason: `the event carries no value for field "${condition.field}"`,
    };
  if (
    normalised === "day_of_week" ||
    normalised === "weekday" ||
    normalised === "dayofweek" ||
    normalised === "day"
  ) {
    const day = String(lookup.value).toLowerCase();
    const expected = condition.value.trim().toLowerCase();
    const inWeek =
      expected === "weekdays"
        ? day !== "saturday" && day !== "sunday"
        : expected === "weekend"
          ? day === "saturday" || day === "sunday"
          : null;
    if (inWeek !== null)
      return inWeek
        ? { matched: true, reason: null }
        : {
            matched: false,
            reason: `expected ${condition.field} to be "${condition.value}" but received "${describeReceived(lookup.value)}"`,
          };
  }
  const candidates = candidatesOf(lookup.value) ?? [];
  const expected = condition.value;
  let expression: RegExp | null = null;
  if (condition.operator === "matches") {
    const oversized = candidates.some(
      (candidate) => candidate.length > maxConditionCandidateLength,
    );
    if (oversized)
      return {
        matched: false,
        reason: `value for field "${condition.field}" exceeds the ${maxConditionCandidateLength}-character regex limit and was not evaluated`,
      };
    try {
      expression = new RegExp(expected);
    } catch {
      return {
        matched: false,
        reason: `pattern "${expected}" for field "${condition.field}" is not a valid expression`,
      };
    }
    if (!isSafeConditionPattern(expected)) {
      return {
        matched: false,
        reason: `pattern "${expected}" for field "${condition.field}" is not a safe expression`,
      };
    }
  }
  const test = (candidate: string): boolean => {
    switch (condition.operator) {
      case "is":
        return candidate === expected;
      case "is_not":
        return candidate !== expected;
      case "starts_with":
        return candidate.startsWith(expected);
      case "contains":
        return candidate.includes(expected);
      case "matches":
        try {
          return expression!.test(candidate);
        } catch {
          return false;
        }
    }
  };
  let matched: boolean;
  try {
    matched =
      condition.operator === "is_not"
        ? candidates.length > 0 && candidates.every(test)
        : candidates.some(test);
  } catch {
    matched = false;
  }
  return matched
    ? { matched: true, reason: null }
    : {
        matched: false,
        reason: `expected ${condition.field} to ${condition.operator.replace(/_/g, " ")} "${expected}" but received "${describeReceived(lookup.value)}"`,
      };
}

export interface ConditionsVerdict {
  matched: boolean;
  reason: string | null;
}

/**
 * Evaluate every condition in order. The first failure names its position and cause, which the
 * delivery history records as the skip reason.
 */
export function evaluateRuleConditions(
  conditions: RuleCondition[],
  event: Record<string, unknown> | null | undefined,
  now: Date = new Date(),
): ConditionsVerdict {
  const payload = event ?? {};
  for (const [index, condition] of conditions.entries()) {
    const verdict = evaluateRuleCondition(condition, payload, now);
    if (!verdict.matched)
      return { matched: false, reason: `Condition ${index + 1} did not match: ${verdict.reason}` };
  }
  return { matched: true, reason: null };
}

export const ruleActionTypes = [
  "start-workflow",
  "ask-zot",
  "run-eval-plan",
  "send-notification",
  "create-work-item",
  "call-webhook",
] as const;
export const ruleActionTypeSchema = z.enum(ruleActionTypes);
export type RuleActionType = z.infer<typeof ruleActionTypeSchema>;

export const ruleActionSchema = z.object({
  type: ruleActionTypeSchema,
  flowId: z.uuid().optional(),
  flowVersionId: z.uuid().optional(),
  flowVersion: z.number().int().min(1).optional(),
  pinFlowVersion: z.boolean().default(true),
  run: automatedRunSettingsSchema.optional(),
  inputsMapping: z.record(z.string(), z.string()).optional(),
  destination: z.string().trim().optional(),
  message: z.string().trim().optional(),
  prompt: z.string().trim().optional(),
});
export type RuleAction = z.infer<typeof ruleActionSchema>;

export const ruleGuardrailsDefaults = {
  concurrencyPolicy: "skip" as const,
  deduplicationWindowMinutes: 10,
  retryWithBackoff: true,
  pauseAfterConsecutiveFailures: 3,
  requireApprovalBeforeStart: false,
};

export const ruleGuardrailsSchema = z.object({
  spendCap: z.number().int().min(0).optional(),
  dailyBudget: z.number().int().min(0).optional(),
  concurrencyPolicy: z.enum(["skip", "queue", "parallel"]).default("skip"),
  deduplicationWindowMinutes: z.number().int().min(0).max(1440).default(10),
  retryWithBackoff: z.boolean().default(true),
  pauseAfterConsecutiveFailures: z.number().int().min(1).default(3),
  requireApprovalBeforeStart: z.boolean().default(false),
  quietHours: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d$/, "Use HH:MM-HH:MM.")
    .nullable()
    .optional(),
});
export type RuleGuardrails = z.infer<typeof ruleGuardrailsSchema>;

const quietHoursPattern = /^([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Project-wide automation limits (AU16). They apply to every automated start
 * on top of each rule's own guardrails, so a rule can tighten them but never
 * relax them. Null means that limit is off. Runs at once and the per-run cap
 * are project settings, shown beside these limits but edited in Project settings.
 */
export const projectAutomationLimitsSchema = z.object({
  dailyBudget: z.number().int().min(1).nullable(),
  quietHours: z.string().regex(quietHoursPattern, "Use HH:MM-HH:MM.").nullable(),
  pauseAfterConsecutiveFailures: z.number().int().min(1).max(10).nullable(),
});
export type ProjectAutomationLimits = z.infer<typeof projectAutomationLimitsSchema>;

export const updateProjectAutomationLimitsSchema = projectAutomationLimitsSchema;
export type UpdateProjectAutomationLimits = z.infer<typeof updateProjectAutomationLimitsSchema>;

export const automationRuleStates = ["active", "paused", "failing"] as const;
export const automationRuleStateSchema = z.enum(automationRuleStates);
export type AutomationRuleState = z.infer<typeof automationRuleStateSchema>;

export const automationRuleSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(1000).nullable().optional(),
  state: automationRuleStateSchema,
  ownerId: z.uuid(),
  trigger: ruleTriggerSchema,
  conditions: z.array(ruleConditionSchema).default([]),
  actions: z.array(ruleActionSchema).min(1),
  guardrails: ruleGuardrailsSchema.default(ruleGuardrailsDefaults),
  nextRunAt: z.string().datetime().nullable(),
  consecutiveFailures: z.number().int().min(0),
  lastRunId: z.uuid().nullable(),
  lastEvent: z.record(z.string(), z.unknown()).nullable().optional(),
  hasSigningSecret: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type AutomationRule = z.infer<typeof automationRuleSchema>;

export const automationRuleListSchema = z.array(automationRuleSchema);
export type AutomationRuleList = z.infer<typeof automationRuleListSchema>;

export const createAutomationRuleSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(1000).nullable().optional(),
  ownerId: z.uuid().optional(),
  trigger: ruleTriggerSchema,
  conditions: z.array(ruleConditionSchema).default([]),
  actions: z.array(ruleActionSchema).min(1),
  guardrails: ruleGuardrailsSchema.default(ruleGuardrailsDefaults),
});
export type CreateAutomationRule = z.infer<typeof createAutomationRuleSchema>;

export const updateAutomationRuleSchema = createAutomationRuleSchema.partial();
export type UpdateAutomationRule = z.infer<typeof updateAutomationRuleSchema>;

/**
 * Typed internal platform events (AU5). The run engine, approvals, spend
 * gate, eval harness, and connection monitor publish these instead of
 * starting work directly. Each type maps to a unified rule trigger type:
 * run events to `run-event`, approval waits to `approval-wait`, spend
 * bands to `spend-threshold`, eval outcomes to `eval-result`, and
 * connection health to `connection-health`.
 */
export const internalEventTypes = [
  "run.failed",
  "run.paused",
  "approval.waiting",
  "spend.threshold",
  "eval.gate_failed",
  "connection.failing",
] as const;
export const internalEventTypeSchema = z.enum(internalEventTypes);
export type InternalEventType = z.infer<typeof internalEventTypeSchema>;

/**
 * One published internal event. The id is the logical dedupe key shared
 * by every subscriber; a redelivery with the same id and bytes replays
 * the recorded admission, while changed bytes conflict. The payload
 * carries only data for condition matching and workflow inputs.
 */
export const internalEventSchema = z.object({
  id: z.string().trim().min(1).max(200),
  type: internalEventTypeSchema,
  projectId: z.uuid(),
  subjectRef: z.string().trim().min(1).max(200),
  payload: z.record(z.string(), z.string()).default({}),
  occurredAt: z.string().datetime().nullable().optional(),
});
export type InternalEvent = z.infer<typeof internalEventSchema>;

/** Spend bands that raise a `spend.threshold` event, as named in the spec. */
export const spendThresholdBands = [50, 80, 100] as const;

/**
 * The rule trigger type each internal event fires. A rule listens for one internal
 * event by naming it in `trigger.event`; a rule that names no event listens for
 * every event of its trigger type.
 */
export const internalEventTriggerTypes = {
  "run.failed": "run-event",
  "run.paused": "run-event",
  "approval.waiting": "approval-wait",
  "spend.threshold": "spend-threshold",
  "eval.gate_failed": "eval-result",
  "connection.failing": "connection-health",
} as const satisfies Record<InternalEventType, RuleTriggerType>;

/**
 * Whether the rule listens for this internal event. The trigger type must be the one
 * the event fires, and a rule that names an event listens for that event only.
 */
export function ruleListensToInternalEvent(trigger: RuleTrigger, type: InternalEventType): boolean {
  if (trigger.type !== internalEventTriggerTypes[type]) return false;
  return trigger.event === undefined || trigger.event === type;
}

/**
 * How long an approval must wait before an `approval.waiting` rule fires, in minutes.
 * A rule without a duration fires as soon as the approval is pending. A malformed
 * duration never fires: an unparsed wait is treated as unmet.
 */
export function approvalWaitMinutes(waitingDuration: string | undefined): number | null {
  if (waitingDuration === undefined) return 0;
  const match = /^(\d{1,4})\s*(m|min|mins|minutes|h|hour|hours|d|day|days)$/i.exec(
    waitingDuration.trim(),
  );
  if (!match || match[1] === undefined) return null;
  const amount = Number(match[1]);
  const unit = (match[2] ?? "m").toLowerCase();
  if (unit.startsWith("h")) return amount * 60;
  if (unit.startsWith("d")) return amount * 60 * 24;
  return amount;
}
